import {
	ConversionsScreen,
	ConversionsSkeleton,
} from "@commerce/insights/ConversionsScreen";
import { config } from "@repo/config";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { orpc } from "@shared/lib/orpc";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

const conversionsSearchSchema = z.object({
	days: z.union([z.literal(7), z.literal(90)]).optional(),
	/** The WhatsApp channel's funnel, cost per order and entry sources. */
	channel: z.literal("whatsapp").optional().catch(undefined),
});

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/conversions",
)({
	validateSearch: conversionsSearchSchema,
	// A range change keeps the previous numbers on screen while the new ones load.
	shouldReload: false,
	loader: async ({ context, location }) => {
		const organizationId = context.organization.id;
		const { days, channel } = conversionsSearchSchema.parse(
			location.search,
		);
		const queryClient = getServerQueryClient();
		const input = { organizationId, days: days ?? 30 };
		if (channel === "whatsapp") {
			await queryClient.ensureQueryData(
				orpc.orders.whatsappConversions.queryOptions({ input }),
			);
		} else {
			await queryClient.ensureQueryData(
				orpc.orders.conversions.queryOptions({ input }),
			);
		}
		return {
			organizationId,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	head: () => ({ meta: [{ title: `Conversions - ${config.appName}` }] }),
	component: ConversionsPage,
});

function ConversionsPage() {
	const { organizationId, dehydratedState } = Route.useLoaderData();
	return (
		<AsyncBoundary
			fallback={<ConversionsSkeleton />}
			dehydratedState={dehydratedState}
		>
			<ConversionsScreen organizationId={organizationId} />
		</AsyncBoundary>
	);
}
